/**
 * CSC native compare drawer (E10).
 *
 * State lives in localStorage under key `csc-compare` as a JSON array:
 *   { id, handle, title, image, price, addedAt }
 *
 * Max 4 items. Adding a 5th is rejected — user must remove one first
 * (we surface a brief inline toast on the rejecting checkbox).
 *
 * On DOMContentLoaded:
 *   1. Hydrate all .csc-compare-checkbox[data-product-id="…"] elements
 *      with the --filled class for any product currently in the set.
 *   2. Render the floating drawer if count >= 1.
 *
 * Public API:
 *   window.cscCompare = {
 *     toggle(btn),       // called from inline onclick
 *     get(),             // returns the array
 *     clear(),           // empties + re-renders
 *     getCount(),
 *     remove(id),
 *   }
 *
 * "Compare now" CTA navigates to /pages/compare?handles=h1,h2,h3,h4 —
 * handles are used (not numeric ids) so the Liquid template can call
 * `all_products[handle]` directly to resolve each product server-side.
 */
(function () {
  if (window.cscCompare) return;

  var STORAGE_KEY = 'csc-compare';
  var MAX_ITEMS = 4;
  var FILLED_CLASS = 'csc-compare-checkbox--filled';

  // ── Storage ─────────────────────────────────────────────────────────
  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }
  function write(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {}
  }
  function emitChange(action, item) {
    document.dispatchEvent(new CustomEvent('compare:changed', {
      detail: { action: action, item: item, count: read().length }
    }));
  }
  function findIndex(list, id) {
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].id) === String(id)) return i;
    }
    return -1;
  }

  // ── Hydration ───────────────────────────────────────────────────────
  function hydrate(scope) {
    var root = scope || document;
    var list = read();
    var idSet = {};
    list.forEach(function (it) { idSet[String(it.id)] = true; });
    var boxes = root.querySelectorAll('.csc-compare-checkbox[data-product-id]');
    for (var i = 0; i < boxes.length; i++) {
      var btn = boxes[i];
      var id = btn.getAttribute('data-product-id');
      var filled = !!idSet[String(id)];
      btn.classList.toggle(FILLED_CLASS, filled);
      btn.setAttribute('aria-pressed', filled ? 'true' : 'false');
      var title = btn.getAttribute('data-product-title') || 'product';
      btn.setAttribute('aria-label',
        (filled ? 'Remove ' : 'Add ') + title +
        (filled ? ' from comparison' : ' to comparison'));
    }
  }

  // ── Toast (inline error feedback on the rejecting checkbox) ─────────
  function showToast(btn, msg) {
    if (!btn) return;
    // Reuse existing toast element on the button if any
    var toast = btn.querySelector('.csc-compare-checkbox__toast');
    if (!toast) {
      toast = document.createElement('span');
      toast.className = 'csc-compare-checkbox__toast';
      toast.setAttribute('role', 'status');
      btn.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.add('csc-compare-checkbox__toast--visible');
    if (btn._cscToastTimer) clearTimeout(btn._cscToastTimer);
    btn._cscToastTimer = setTimeout(function () {
      toast.classList.remove('csc-compare-checkbox__toast--visible');
    }, 2400);
  }

  // ── Toggle ──────────────────────────────────────────────────────────
  function toggle(btn) {
    if (!btn || !btn.getAttribute) return;
    var id = btn.getAttribute('data-product-id');
    if (!id) return;

    var list = read();
    var idx = findIndex(list, id);
    var action;
    var item;

    if (idx >= 0) {
      // Remove
      item = list[idx];
      list.splice(idx, 1);
      action = 'remove';
    } else {
      // Add — reject if cap reached
      if (list.length >= MAX_ITEMS) {
        showToast(btn, 'Max 4 — remove one to add another');
        return;
      }
      // Capture full snapshot including all spec metafields. The compare
      // page renders client-side from this — Shopify CDN strips query
      // strings on /pages/* so server-side Liquid params don't work.
      item = {
        id: id,
        handle: btn.getAttribute('data-product-handle') || '',
        title: btn.getAttribute('data-product-title') || '',
        image: btn.getAttribute('data-product-image') || '',
        price: btn.getAttribute('data-product-price') || '',
        url: btn.getAttribute('data-product-url') || ('/products/' + (btn.getAttribute('data-product-handle') || '')),
        specs: {
          seats: btn.getAttribute('data-spec-seats') || '',
          jet_count: btn.getAttribute('data-spec-jet-count') || '',
          pumps_hp: btn.getAttribute('data-spec-pumps-hp') || '',
          sanitation: btn.getAttribute('data-spec-sanitation') || '',
          dimensions_cm: btn.getAttribute('data-spec-dimensions-cm') || '',
          warranty_years: btn.getAttribute('data-spec-warranty-years') || '',
          warranty_summary: btn.getAttribute('data-spec-warranty-summary') || ''
        },
        addedAt: Date.now()
      };
      list.push(item);
      action = 'add';
    }

    write(list);
    hydrate();
    emitChange(action, item);
  }

  function remove(id) {
    var list = read();
    var idx = findIndex(list, id);
    if (idx < 0) return;
    var item = list[idx];
    list.splice(idx, 1);
    write(list);
    hydrate();
    emitChange('remove', item);
  }

  function clearAll() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    hydrate();
    emitChange('clear', null);
  }

  // ── Drawer renderer ─────────────────────────────────────────────────
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c];
    });
  }

  function renderDrawer() {
    var drawer = document.getElementById('csc-compare-drawer');
    if (!drawer) return;
    var list = read();

    if (list.length === 0) {
      drawer.classList.remove('csc-compare-drawer--visible');
      drawer.setAttribute('hidden', '');
      return;
    }
    drawer.removeAttribute('hidden');
    // Defer adding the visible class to the next frame so the CSS
    // transition (translateY from 100% → 0) actually animates instead
    // of snapping when going from hidden to visible.
    requestAnimationFrame(function () {
      drawer.classList.add('csc-compare-drawer--visible');
    });

    var counter = document.getElementById('csc-compare-counter');
    if (counter) counter.textContent = list.length + ' of ' + MAX_ITEMS;

    var cards = document.getElementById('csc-compare-drawer-cards');
    if (cards) {
      cards.innerHTML = list.map(function (it) {
        var titleShort = it.title.length > 28 ? it.title.slice(0, 27) + '…' : it.title;
        return '<div class="csc-compare-mini-card" data-product-id="' + escapeHtml(it.id) + '">'
          + (it.image ? '<img src="' + escapeHtml(it.image) + '" alt="" loading="lazy">' : '')
          + '<span class="csc-compare-mini-card__title">' + escapeHtml(titleShort) + '</span>'
          + '<button type="button" class="csc-compare-mini-card__remove" data-remove-id="' + escapeHtml(it.id) + '" aria-label="Remove ' + escapeHtml(it.title) + ' from comparison">×</button>'
          + '</div>';
      }).join('');
    }

    var cta = document.getElementById('csc-compare-cta');
    if (cta) {
      var handles = list.map(function (it) { return encodeURIComponent(it.handle || ''); }).filter(Boolean).join(',');
      cta.setAttribute('href', '/pages/compare?handles=' + handles);
      cta.setAttribute('aria-disabled', list.length < 2 ? 'true' : 'false');
      cta.classList.toggle('csc-compare-cta--disabled', list.length < 2);
    }
  }

  // ── Init ────────────────────────────────────────────────────────────
  function bindDrawerActions() {
    var clearBtn = document.getElementById('csc-compare-clear');
    if (clearBtn && !clearBtn._cscBound) {
      clearBtn._cscBound = true;
      clearBtn.addEventListener('click', function (e) {
        e.preventDefault();
        clearAll();
      });
    }
    var cards = document.getElementById('csc-compare-drawer-cards');
    if (cards && !cards._cscBound) {
      cards._cscBound = true;
      cards.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-remove-id]');
        if (!btn) return;
        e.preventDefault();
        remove(btn.getAttribute('data-remove-id'));
      });
    }
    var cta = document.getElementById('csc-compare-cta');
    if (cta && !cta._cscBound) {
      cta._cscBound = true;
      cta.addEventListener('click', function (e) {
        // Prevent navigation if fewer than 2 items selected (Compare needs ≥2)
        if (read().length < 2) {
          e.preventDefault();
        }
      });
    }
  }

  // ── Compare page renderer ───────────────────────────────────────────
  function moneyFromCents(cents) {
    if (cents === '' || cents == null) return '';
    var n = parseInt(cents, 10);
    if (isNaN(n)) return '';
    var pounds = n / 100;
    var formatted = pounds % 1 === 0 ? pounds.toFixed(0) : pounds.toFixed(2);
    return '£' + formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // Spec row definitions: [label, key on item.specs OR 'price']
  var SPEC_ROWS = [
    ['Price',            'price'],
    ['Seats',            'seats'],
    ['Jets',             'jet_count'],
    ['Pump HP',          'pumps_hp'],
    ['Sanitation',       'sanitation'],
    ['Dimensions (cm)',  'dimensions_cm'],
    ['Warranty (years)', 'warranty_years'],
    ['Warranty summary', 'warranty_summary', 'wrap']
  ];

  function valueFor(item, key) {
    if (key === 'price') return moneyFromCents(item.price);
    var v = (item.specs && item.specs[key]) ? item.specs[key] : '';
    // pumps_hp is number_decimal ("10.0") — strip the trailing .0 so the
    // "Pump HP" row reads "10" like the Seats/Jets rows and the card chip
    // ("10 HP"), keeping the compare table and the collection card in sync.
    if (key === 'pumps_hp' && v) {
      var n = parseFloat(v);
      if (!isNaN(n)) v = String(n);
    }
    return v;
  }

  function renderComparePage() {
    var mount = document.querySelector('[data-compare-page]');
    if (!mount) return;
    var list = read();

    var emptyEl = document.getElementById('csc-compare-page-empty');
    var tableMount = document.getElementById('csc-compare-page-table');

    if (list.length === 0) {
      if (emptyEl) emptyEl.hidden = false;
      if (tableMount) tableMount.innerHTML = '';
      return;
    }
    if (emptyEl) emptyEl.hidden = true;
    if (!tableMount) return;

    // Build the table
    var html = ['<div class="csc-compare-page__controls">'];
    html.push('<label class="csc-compare-page__diff-toggle"><input type="checkbox" id="csc-compare-diff-only"> <span>Show differences only</span></label>');
    html.push('</div>');
    html.push('<div class="csc-compare-table-wrap"><table class="csc-compare-table" id="csc-compare-table"><thead><tr>');
    html.push('<th class="csc-compare-table__corner" scope="col"><span class="csc-compare-table__corner-label">Spec</span></th>');
    list.forEach(function (it) {
      html.push('<th class="csc-compare-table__product-cell" scope="col">');
      html.push('<a class="csc-compare-table__product-link" href="' + escapeHtml(it.url) + '">');
      if (it.image) html.push('<img class="csc-compare-table__product-image" src="' + escapeHtml(it.image) + '" alt="' + escapeHtml(it.title) + '" loading="lazy">');
      html.push('<span class="csc-compare-table__product-title">' + escapeHtml(it.title) + '</span>');
      html.push('<span class="csc-compare-table__product-price">' + moneyFromCents(it.price) + '</span>');
      html.push('</a>');
      html.push('<button type="button" class="csc-compare-table__remove" data-compare-remove-id="' + escapeHtml(it.id) + '" aria-label="Remove ' + escapeHtml(it.title) + ' from comparison">Remove</button>');
      html.push('</th>');
    });
    html.push('</tr></thead><tbody>');
    SPEC_ROWS.forEach(function (row) {
      var label = row[0], key = row[1], hint = row[2];
      var rowClass = 'csc-compare-table__row' + (hint === 'wrap' ? ' csc-compare-table__row--wrap' : '');
      html.push('<tr class="' + rowClass + '" data-spec-key="' + key + '">');
      html.push('<th class="csc-compare-table__spec-label" scope="row">' + label + '</th>');
      list.forEach(function (it) {
        var v = valueFor(it, key);
        html.push('<td class="csc-compare-table__spec-cell" data-spec-value="' + escapeHtml(v) + '">');
        if (!v) html.push('<span class="csc-compare-table__missing">—</span>');
        else html.push(escapeHtml(v));
        html.push('</td>');
      });
      html.push('</tr>');
    });
    html.push('</tbody></table></div>');
    tableMount.innerHTML = html.join('');

    // Wire interactions
    var diffEl = document.getElementById('csc-compare-diff-only');
    if (diffEl) {
      diffEl.addEventListener('change', function () {
        var enabled = diffEl.checked;
        var table = document.getElementById('csc-compare-table');
        if (!table) return;
        table.querySelectorAll('tbody tr').forEach(function (tr) {
          if (!enabled) { tr.style.display = ''; return; }
          var cells = tr.querySelectorAll('.csc-compare-table__spec-cell');
          var values = Array.from(cells).map(function (c) {
            return (c.getAttribute('data-spec-value') || '').trim();
          });
          var allSame = values.length > 1 && values.every(function (v) { return v === values[0]; });
          tr.style.display = allSame ? 'none' : '';
        });
      });
    }
    tableMount.querySelectorAll('[data-compare-remove-id]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-compare-remove-id');
        remove(id);
        renderComparePage();
      });
    });
  }

  function init() {
    hydrate();
    renderDrawer();
    bindDrawerActions();
    renderComparePage();
    document.addEventListener('compare:changed', function () {
      renderDrawer();
      bindDrawerActions();
      renderComparePage();
    });
    // Cross-tab sync
    window.addEventListener('storage', function (e) {
      if (e.key === STORAGE_KEY) {
        hydrate();
        renderDrawer();
        renderComparePage();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.cscCompare = {
    toggle: toggle,
    get: read,
    clear: clearAll,
    getCount: function () { return read().length; },
    remove: remove
  };
})();
